import { useEffect, useState } from "react";

const PAGE_DEFINITIONS = {
  dashboard: {
    pageId: "page.facility.dashboard",
    pageTitle: "Dashboard",
  },
  "utility-summary": {
    pageId: "page.facility.utility.summary",
    pageTitle: "Utility Summary",
  },
  savings: {
    pageId: "page.facility.savings",
    pageTitle: "Savings",
  },
};

const pages = new Map();
const listeners = new Set();
let activePage = null;

const emit = () => listeners.forEach((listener) => listener());
const pageKey = (companyId, facilityId, pageId) =>
  `${String(companyId)}:${String(facilityId)}:${pageId}`;

const safeText = (value, limit) => String(value || "").replace(/\0/g, " ").trim().slice(0, limit);
const finiteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const sanitizePoints = (points) =>
  (Array.isArray(points) ? points : [])
    .slice(0, 240)
    .map((point) => {
      const value = finiteNumber(point?.value);
      if (value === null) return null;
      const numericX = typeof point?.x === "number" ? finiteNumber(point.x) : null;
      return {
        label: safeText(point?.label, 120) || null,
        x: numericX !== null ? numericX : safeText(point?.x, 120) || null,
        value,
      };
    })
    .filter(Boolean);

const sanitizeChart = (chart) => ({
  chart_id: safeText(chart?.chartId, 120),
  title: safeText(chart?.title, 160),
  aliases: (Array.isArray(chart?.aliases) ? chart.aliases : [])
    .slice(0, 8)
    .map((item) => safeText(item, 120))
    .filter(Boolean),
  chart_type: safeText(chart?.chartType || "chart", 40),
  description: safeText(chart?.description, 500),
  filters: Object.fromEntries(
    Object.entries(chart?.filters || {})
      .slice(0, 16)
      .filter(([, value]) => value == null || ["string", "number", "boolean"].includes(typeof value))
      .map(([key, value]) => [safeText(key, 80), typeof value === "string" ? safeText(value, 300) : value])
  ),
  source_operation_ids: (Array.isArray(chart?.sourceOperationIds) ? chart.sourceOperationIds : [])
    .slice(0, 12)
    .map((item) => safeText(item, 120))
    .filter(Boolean),
  series: (Array.isArray(chart?.series) ? chart.series : [])
    .slice(0, 12)
    .map((series) => ({
      name: safeText(series?.name, 120),
      unit: safeText(series?.unit, 40) || null,
      role: safeText(series?.role, 80) || null,
      visible: series?.visible !== false,
      points: sanitizePoints(series?.points),
    }))
    .filter((series) => series.name),
  metrics: (Array.isArray(chart?.metrics) ? chart.metrics : [])
    .slice(0, 16)
    .map((metric) => {
      const value = finiteNumber(metric?.value);
      if (!safeText(metric?.name, 120) || value === null) return null;
      return {
        name: safeText(metric.name, 120),
        value,
        unit: safeText(metric?.unit, 40) || null,
      };
    })
    .filter(Boolean),
  completeness: ["complete", "partial", "unknown"].includes(chart?.completeness)
    ? chart.completeness
    : "unknown",
  limitations: (Array.isArray(chart?.limitations) ? chart.limitations : [])
    .slice(0, 12)
    .map((item) => safeText(item, 300))
    .filter(Boolean),
});

const routeDescriptor = (pathname) => {
  const parts = String(pathname || "").split("/").filter(Boolean);
  if (parts[0] !== "facility" || parts.length < 4) return null;
  const [, companyId, facilityId, leaf] = parts;
  if (leaf === "assistant") return { assistant: true };
  const definition = PAGE_DEFINITIONS[leaf];
  if (!definition) return null;
  return {
    ...definition,
    companyId: String(companyId),
    facilityId: String(facilityId),
    route: `/${parts.join("/")}`,
  };
};

export const setActiveAiPageRoute = (pathname) => {
  const descriptor = routeDescriptor(pathname);
  // The Assistant is not itself a source page. Clear the previous page when
  // navigating here so a stale dashboard/chart snapshot cannot be submitted
  // as current-page context and rejected by the Backend with HTTP 400.
  if (descriptor?.assistant) {
    activePage = null;
    emit();
    return;
  }
  activePage = descriptor;
  emit();
};

export const registerAiChartContext = ({ companyId, facilityId, pageId, chart }) => {
  if (!companyId || !facilityId || !pageId || !chart) return;
  const sanitized = sanitizeChart(chart);
  if (!sanitized.chart_id || !sanitized.title) return;
  const key = pageKey(companyId, facilityId, pageId);
  const existing = pages.get(key) || { charts: new Map(), capturedAt: null };
  existing.charts.set(sanitized.chart_id, sanitized);
  existing.capturedAt = new Date().toISOString();
  pages.set(key, existing);
  emit();
};

export const unregisterAiChartContext = ({ companyId, facilityId, pageId, chartId }) => {
  if (!companyId || !facilityId || !pageId || !chartId) return;
  const key = pageKey(companyId, facilityId, pageId);
  const existing = pages.get(key);
  if (!existing) return;
  existing.charts.delete(String(chartId));
  if (!existing.charts.size) {
    pages.delete(key);
  } else {
    pages.set(key, existing);
  }
  emit();
};

export const getCurrentAiPageContext = (companyId, facilityId) => {
  if (
    !activePage ||
    String(companyId) !== activePage.companyId ||
    String(facilityId) !== activePage.facilityId
  ) {
    return null;
  }
  const stored = pages.get(pageKey(companyId, facilityId, activePage.pageId));
  const charts = stored ? Array.from(stored.charts.values()).slice(0, 12) : [];
  if (!charts.length) return null;
  return {
    version: 1,
    page_id: activePage.pageId,
    page_title: activePage.pageTitle,
    route: activePage.route,
    captured_at: stored.capturedAt,
    company_id: Number(companyId),
    facility_id: Number(facilityId),
    charts,
  };
};

export const subscribeAiPageContext = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useCurrentAiPageContext = (companyId, facilityId) => {
  const [snapshot, setSnapshot] = useState(() => getCurrentAiPageContext(companyId, facilityId));
  useEffect(() => {
    const update = () => setSnapshot(getCurrentAiPageContext(companyId, facilityId));
    update();
    return subscribeAiPageContext(update);
  }, [companyId, facilityId]);
  return snapshot;
};

export const resetAiPageContextStore = () => {
  pages.clear();
  activePage = null;
  emit();
};
